import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Button, Tabs, Tab, Form, Row, Col, Toast } from "react-bootstrap";
import API_ENDPOINTS from "../../config/api";

const API_URL = API_ENDPOINTS.url;

const getImageUrl = (path) => {
  if (!path) return null;

  // Nếu đường dẫn đã có sẵn (bắt đầu bằng http:// hoặc https://), trả về đường dẫn đó
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  // Nếu đường dẫn bắt đầu bằng "/uploads/", chỉ cần thêm API_URL vào
  const cleanPath = path.startsWith("/uploads/") ? path.slice(8) : path;

  // Trả về URL hoàn chỉnh
  return `${API_URL}/uploads/${cleanPath}`;
};

const EmployeeModal = ({ show, onClose, onSave, employee = {}, departments = [], positions = [] }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [wards, setWards] = useState([]);
  const prevShow = useRef(false);

  const formatDate = (date) => date ? new Date(date).toISOString().substring(0, 10) : "";

  // ======== Load provinces =========
  useEffect(() => {
    fetch("https://provinces.open-api.vn/api/p/")
      .then(res => res.json())
      .then(data => setProvinces(data))
      .catch(err => console.error("Error fetching provinces:", err));
  }, []);

  // ======== Load districts =========
  useEffect(() => {
    if (formData.Province) {
      const province = provinces.find(p => p.name === formData.Province);
      if (province?.code) {
        fetch(`https://provinces.open-api.vn/api/p/${province.code}?depth=2`)
          .then(res => res.json())
          .then(data => setDistricts(data.districts || []))
          .catch(err => console.error("Error fetching districts:", err));
      }
    } else {
      setDistricts([]);
      setWards([]);
    }
  }, [formData.Province]);

  // ======== Load wards =========
  useEffect(() => {
    if (formData.District) {
      const district = districts.find(d => d.name === formData.District);
      if (district?.code) {
        fetch(`https://provinces.open-api.vn/api/d/${district.code}?depth=2`)
          .then(res => res.json())
          .then(data => setWards(data.wards || []))
          .catch(err => console.error("Error fetching wards:", err));
      }
    } else {
      setWards([]);
    }
  }, [formData.District]);

  // ======== Init form when modal opens =========
  useEffect(() => {
    if (show && !prevShow.current) {
      setFormData({
        id: employee?.id || null,
        EmployeeCode: employee?.employeeCode || "",
        Name: employee?.name || "",
        Gender: employee?.gender || "",
        DateOfBirth: formatDate(employee?.dateOfBirth),
        IdCardNumber: employee?.idCardNumber || "",
        PhoneNumber: employee?.phoneNumber || "",
        StartDate: formatDate(employee?.startDate),
        EndDate: formatDate(employee?.endDate),
        DepartmentCode: employee?.departmentCode || "",
        PositionCode: employee?.positionCode || "",
        ProfileImageUrl: employee?.profileImageUrl || "",
        AdvanceSalaryLimit: employee?.advanceSalaryLimit || 0,
        IsWarehouseManager: employee?.isWarehouseManager || false,
        IsHRManager: employee?.isHRManager || false,
        BasicSalary: employee?.basicSalary || 0,
        FuelAllowance: employee?.fuelAllowance || 0,
        LivingAllowance: employee?.livingAllowance || 0,
        PhoneAllowance: employee?.phoneAllowance || 0,
        KPIBonus: employee?.kpiBonus || 0,
        OtherBonus: employee?.otherBonus || 0,
        SeniorityBonus: employee?.seniorityBonus || 0,
        CardIssuingFee: employee?.cardIssuingFee || 0,
        TotalAmount: employee?.totalAmount || 0,
        SalaryAdvanceRequest: employee?.salaryAdvanceRequest || 0,
        SalaryPaymentMethod: employee?.salaryPaymentMethod || "",
        Address: employee?.address || "",
        ATMAccount: employee?.atmAccount || "",
        BankName: employee?.bankName || "",
        ChineseName: employee?.chineseName || "",
        IDIssueDate: formatDate(employee?.idIssueDate),
        IDIssuePlace: employee?.idIssuePlace || "",
        DetailedAddress: employee?.detailedAddress || "",
        Province: employee?.province || "",
        District: employee?.district || "",
        Ward: employee?.ward || "",
        ZaloVerified: employee?.zaloVerified || "",
        DependentsCount: employee?.dependentsCount || 0,
        DependentEndDate1: formatDate(employee?.dependentEndDate1),
        DependentEndDate2: formatDate(employee?.dependentEndDate2),
        Rehired: employee?.rehired || false,
        NotRehired: employee?.notRehired || false,
        InsuranceSalary: employee?.insuranceSalary || 0,
        InsuranceStatus: employee?.insuranceStatus || "",
        InsuranceNumber: employee?.insuranceNumber || "",
        InsuranceStartDate: formatDate(employee?.insuranceStartDate),
        UnionBranch: employee?.unionBranch || "",
        MagneticCardNumber: employee?.magneticCardNumber || "",
        SalaryLevel: employee?.salaryLevel || "",
        Email: employee?.email || "",
        Status: employee?.status || "",
        Notes: employee?.notes || "",
        TerminationDecision: employee?.terminationDecision || "",
        ReductionStatus: employee?.reductionStatus || ""
      });
      setErrors({});
      setSelectedFile(null);
      setPreviewImage(employee?.profileImageUrl ? getImageUrl(employee.profileImageUrl) : null);
    }
    prevShow.current = show;
  }, [show, employee, provinces, districts, wards]);

  // ======== Update DetailedAddress =========
  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined })); // Remove error message
  }, [errors]);

  // ======== Avoid unnecessary state updates in useEffect ========
  useEffect(() => {
    const { Province, District, Ward } = formData;
    if (Province && District && Ward) {
      const newDetailedAddress = `${Ward}, ${District}, ${Province}`;
      if (formData.DetailedAddress !== newDetailedAddress) {
        handleChange("DetailedAddress", newDetailedAddress);
      }
    }
  }, [formData.Province, formData.District, formData.Ward, formData.DetailedAddress, handleChange]);

  // ======== File change =========
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      handleChange("ProfileImage", file);

      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleProvinceChange = (provinceName) => {
    handleChange("Province", provinceName);
    setDistricts([]);
    setWards([]);
    handleChange("District", "");
    handleChange("Ward", "");
  };

  const handleDistrictChange = (districtName) => {
    handleChange("District", districtName);
    setWards([]);
    handleChange("Ward", "");
  };

  const handleWardChange = (wardName) => {
    handleChange("Ward", wardName);
  };

  const requiredFields = [
    "EmployeeCode", "Name", "IdCardNumber", "PhoneNumber", "DepartmentCode", "PositionCode"
  ];

  const renderField = (label, field, type = "text", col = 6, options = []) => (
    <Col md={col} className="mb-3" key={field}>
      <Form.Group>
        {type === "checkbox" ? (
          <Form.Check
            type="checkbox"
            label={label}
            checked={!!formData[field]}
            onChange={e => handleChange(field, e.target.checked)}
          />
        ) : type === "file" ? (
          <>
            <Form.Label>{label}</Form.Label>
            <Form.Control type="file" onChange={handleFileChange} isInvalid={!!errors[field]} />
            <Form.Control.Feedback type="invalid">{errors[field]}</Form.Control.Feedback>
            {previewImage && (
              <img 
                src={typeof previewImage === "string" && !previewImage.startsWith("data:") 
                      ? getImageUrl(previewImage)  // Dùng getImageUrl để tạo URL hợp lệ
                      : previewImage} 
                alt="Preview" 
                style={{ maxWidth: "150px", marginTop: "10px" }} 
              />
            )}
          </>
        ) : (
          <>
            <Form.Label>
              {label}
              {requiredFields.includes(field) && <span style={{ color: "red" }}> *</span>}
            </Form.Label>
            {type === "select" ? (
              <Form.Select value={formData[field] ?? ""} onChange={e => handleChange(field, e.target.value)} isInvalid={!!errors[field]}>
                <option value="">Chọn {label.toLowerCase()}</option>
                {options.map(opt => (
                  <option key={opt.departmentCode || opt.positionCode || opt.Code || opt.value} value={opt.departmentCode || opt.positionCode || opt.Code || opt.value}>
                    {opt.name || opt.label || opt.Name}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Control type={type} value={formData[field] || ""} onChange={e => handleChange(field, e.target.value)} isInvalid={!!errors[field]} />
            )}
            <Form.Control.Feedback type="invalid">{errors[field]}</Form.Control.Feedback>
          </>
        )}
      </Form.Group>
    </Col>
  );

  const validateFields = () => {
    const newErrors = {};
    requiredFields.forEach(field => {
      if (!formData[field] || formData[field].toString().trim() === "") {
        let label = field;
        if (field === "DepartmentCode") label = "Phòng ban";
        if (field === "PositionCode") label = "Chức vụ";
        newErrors[field] = `${label} không được để trống`;
      }
    });
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Kiểm tra dữ liệu đầu vào
    const newErrors = validateFields();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Chuẩn bị formData
    const formDataToSend = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (value === null || value === '') {
        if (key === 'id') {
          value = null;
        } else {
          return;
        }
      }
      formDataToSend.append(key, value);
    });

    if (formData.ProfileImage && formData.ProfileImage instanceof File) {
      formDataToSend.append('ProfileImage', formData.ProfileImage);
    }

    try {
      await onSave(formDataToSend);
      setShowToast(true);
      onClose();
    } catch (err) {
      console.error("❌ Lỗi khi lưu nhân viên:", err);
    }
  };
  return (
    <>
      <Modal show={show} onHide={onClose} size="lg">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Thông tin nhân viên</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Tabs defaultActiveKey="basic">
              <Tab eventKey="basic" title="Thông tin cơ bản">
                <Row className="mt-3">
                  {renderField("Mã nhân viên", "EmployeeCode")}
                  {renderField("Họ tên", "Name")}
                  {renderField("Giới tính", "Gender", "select", 6, [
                    { value: "Nam", label: "Nam" },
                    { value: "Nữ", label: "Nữ" }
                  ])}
                  {renderField("Ngày sinh", "DateOfBirth", "date")}
                  {renderField("CMND/CCCD", "IdCardNumber")}
                  {renderField("Số điện thoại", "PhoneNumber")}
                  {renderField("Ngày vào làm", "StartDate", "date")}
                  {renderField("Ngày nghỉ việc", "EndDate", "date")}
                  {renderField("Phòng ban", "DepartmentCode", "select", 6, departments)}
                  {renderField("Chức vụ", "PositionCode", "select", 6, positions)}
                  {renderField("Ảnh đại diện", "ProfileImageUrl", "file")}
                  {renderField("Giới hạn tạm ứng", "AdvanceSalaryLimit", "number")}
                  {renderField("Quản lý kho", "IsWarehouseManager", "checkbox")}
                  {renderField("Quản lý nhân sự", "IsHRManager", "checkbox")}
                </Row>
              </Tab>

              <Tab eventKey="salary" title="Thông tin lương">
                <Row className="mt-3">
                  {renderField("Lương cơ bản", "BasicSalary", "number")}
                  {renderField("Phụ cấp xăng xe", "FuelAllowance", "number")}
                  {renderField("Phụ cấp sinh hoạt", "LivingAllowance", "number")}
                  {renderField("Phụ cấp điện thoại", "PhoneAllowance", "number")}
                  {renderField("Thưởng KPI", "KPIBonus", "number")}
                  {renderField("Thưởng khác", "OtherBonus", "number")}
                  {renderField("Thưởng thâm niên", "SeniorityBonus", "number")}
                  {renderField("Phí phát hành thẻ", "CardIssuingFee", "number")}
                  {renderField("Tổng cộng", "TotalAmount", "number")}
                  {renderField("Yêu cầu tạm ứng", "SalaryAdvanceRequest", "number")}
                  {renderField("Phương thức thanh toán", "SalaryPaymentMethod")}
                </Row>
              </Tab>

              <Tab eventKey="additional" title="Thông tin bổ sung">
                <Row className="mt-3">
                  {renderField("Số người phụ thuộc", "DependentsCount", "number")}
                  {renderField("Ngày kết thúc phụ thuộc 1", "DependentEndDate1", "date")}
                  {renderField("Ngày kết thúc phụ thuộc 2", "DependentEndDate2", "date")}
                  {renderField("Lương bảo hiểm", "InsuranceSalary", "number")}
                  {renderField("Trạng thái bảo hiểm", "InsuranceStatus")}
                  {renderField("Số bảo hiểm", "InsuranceNumber")}
                  {renderField("Ngày tham gia bảo hiểm", "InsuranceStartDate", "date")}
                  {renderField("Chi nhánh công đoàn", "UnionBranch")}
                  {renderField("Số thẻ từ", "MagneticCardNumber")}
                  {renderField("Cấp lương", "SalaryLevel")}
                  {renderField("Số tài khoản ATM", "ATMAccount")}
                  {renderField("Tên ngân hàng", "BankName")}
                  {renderField("Tên tiếng Trung", "ChineseName")}
                  {renderField("Ngày cấp CMND/CCCD", "IDIssueDate", "date")}
                  {renderField("Nơi cấp CMND/CCCD", "IDIssuePlace")}
                  {renderField("Xác minh Zalo", "ZaloVerified")}
                </Row>

                <Col md={12} className="mt-4">
                  <h5>Địa chỉ</h5>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Tỉnh/Thành phố</Form.Label>
                        <Form.Select value={formData.Province || ""} onChange={e => handleProvinceChange(e.target.value)}>
                          <option value="">Chọn tỉnh/thành phố</option>
                          {provinces.map(p => (
                            <option key={p.code} value={p.name}>{p.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Quận/Huyện</Form.Label>
                        <Form.Select value={formData.District || ""} onChange={e => handleDistrictChange(e.target.value)}>
                          <option value="">Chọn quận/huyện</option>
                          {districts.map(d => (
                            <option key={d.code} value={d.name}>{d.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phường/Xã</Form.Label>
                        <Form.Select value={formData.Ward || ""} onChange={e => handleWardChange(e.target.value)}>
                          <option value="">Chọn phường/xã</option>
                          {wards.map(w => (
                            <option key={w.code} value={w.name}>{w.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={12}>{renderField("Địa chỉ chi tiết", "DetailedAddress")}</Col>
                  </Row>
                </Col>

                <Col md={12} className="mt-4">
                  <h5>Tuyển dụng</h5>
                  <Row>
                    {renderField("Tuyển lại", "Rehired", "checkbox")}
                    {renderField("Không tuyển lại", "NotRehired", "checkbox")}
                  </Row>
                </Col>
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onClose}>Hủy</Button>
            <Button type="submit" variant="primary">Lưu</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Toast style={{ position: "fixed", top: 20, right: 20 }} show={showToast} onClose={() => setShowToast(false)} delay={2000} autohide>
        <Toast.Body>Lưu nhân viên thành công!</Toast.Body>
      </Toast>
    </>
  );
};

export default EmployeeModal;
